import {createContext, type ReactNode, use, useCallback, useState} from 'react'

import {getFieldsForCapability} from '../../components/saved-view-options/get-fields-for-capability'
import {Capability} from '../../components/saved-view-options/fields'
import {useFields} from './FieldsProvider'

type SetVisibleField = (identifier: string, visible: boolean) => void

const VisibleFieldIdentifiersContext = createContext<ReadonlySet<string> | null>(null)
const SetVisibleFieldContext = createContext<SetVisibleField | null>(null)

/**
 * Standalone visibility state. Seeded once from
 * initialIdentifiers, or all attributable fields from FieldsProvider when omitted.
 * Later prop/metadata changes do not reset the selection; the host keys it by view identity.
 *
 * Planning supplies explicit defaults so menu metadata cannot block or reset the cards.
 * Board wrappers pass the resulting identifiers to cards.
 *
 * Identifiers and the setter use separate contexts so setter-only consumers do not subscribe
 * to selection changes.
 */
export function VisibleFieldsProvider({
  children,
  initialIdentifiers,
}: {
  children: ReactNode
  initialIdentifiers?: readonly string[]
}) {
  return initialIdentifiers ? (
    <VisibleFieldsState initialIdentifiers={initialIdentifiers}>{children}</VisibleFieldsState>
  ) : (
    <DefaultVisibleFields>{children}</DefaultVisibleFields>
  )
}

function DefaultVisibleFields({children}: {children: ReactNode}) {
  const view = useFields()
  return (
    <VisibleFieldsState
      initialIdentifiers={getFieldsForCapability(view, Capability.ATTRIBUTABLE).map(field => field.identifier)}
    >
      {children}
    </VisibleFieldsState>
  )
}

function VisibleFieldsState({
  initialIdentifiers,
  children,
}: {
  initialIdentifiers: readonly string[]
  children: ReactNode
}) {
  const [identifiers, setIdentifiers] = useState<ReadonlySet<string>>(() => new Set(initialIdentifiers))

  const setVisible = useCallback<SetVisibleField>((identifier, visible) => {
    setIdentifiers(current => {
      if (visible === current.has(identifier)) return current
      const next = new Set(current)
      if (visible) next.add(identifier)
      else next.delete(identifier)
      return next
    })
  }, [])

  return (
    <SetVisibleFieldContext value={setVisible}>
      <VisibleFieldIdentifiersContext value={identifiers}>{children}</VisibleFieldIdentifiersContext>
    </SetVisibleFieldContext>
  )
}

export function useVisibleFieldIdentifiers() {
  const value = use(VisibleFieldIdentifiersContext)
  if (value === null) throw new Error('useVisibleFieldIdentifiers must be used within <VisibleFieldsProvider>')
  return value
}

export function useSetVisibleField() {
  const value = use(SetVisibleFieldContext)
  if (value === null) throw new Error('useSetVisibleField must be used within <VisibleFieldsProvider>')
  return value
}
